import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Layers, GraduationCap, Activity, Brain, Plus, Pencil, Trash2 } from 'lucide-react';
import { useT } from '@/store/i18n';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Tabs } from '@/components/ui/Tabs';
import { Modal } from '@/components/ui/Modal';
import { confirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/components/ui/Toast';
import { api } from '@/lib/api';
import type {
  SubjectGroup, GradeLevel, DifficultyLevel, CognitiveLevel,
} from '@/types';

interface MetaPayload {
  subject_groups?: SubjectGroup[];
  grade_levels?: GradeLevel[];
  difficulty_levels?: DifficultyLevel[];
  cognitive_levels?: CognitiveLevel[];
}

type ItemType = 'subject-groups' | 'grade-levels' | 'difficulty-levels' | 'cognitive-levels';

interface EditTarget {
  type: ItemType;
  row: any;
}

export function Metadata() {
  const t = useT();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<EditTarget | null>(null);

  const meta = useQuery({
    queryKey: ['metadata-full'],
    queryFn: () => api<{ data?: MetaPayload } & MetaPayload>('metadata/all.php').then((r) => r.data ?? r),
  });

  const save = useMutation({
    mutationFn: async (input: { type: ItemType; data: any; id?: number }) => {
      const url = input.id
        ? `metadata/index.php?endpoint=${input.type}&id=${input.id}`
        : `metadata/index.php?endpoint=${input.type}`;
      return api(url, { method: input.id ? 'PUT' : 'POST', json: input.data });
    },
    onSuccess: (_, vars) => {
      toast.success(vars.id ? t('metadata.success_edit') : t('metadata.success_add'));
      qc.invalidateQueries({ queryKey: ['metadata-full'] });
      setEditing(null);
    },
    onError: (e: Error) => toast.error(t('error.save_failed'), e.message),
  });

  const remove = useMutation({
    mutationFn: (input: { type: ItemType; id: number }) =>
      api(`metadata/index.php?endpoint=${input.type}&id=${input.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success(t('metadata.success_delete'));
      qc.invalidateQueries({ queryKey: ['metadata-full'] });
    },
    onError: (e: Error) => toast.error(t('error.delete_failed'), e.message),
  });

  function handleDelete(type: ItemType, id: number, label: string) {
    confirmDialog({
      title: `${t('btn.delete')} "${label}"?`,
      description: t('metadata.irreversible'),
      destructive: true,
      confirmLabel: t('btn.delete'),
    }).then((ok) => { if (ok) remove.mutate({ type, id }); });
  }

  const subjects = meta.data?.subject_groups ?? [];
  const grades = meta.data?.grade_levels ?? [];
  const difficulties = meta.data?.difficulty_levels ?? [];
  const cognitives = meta.data?.cognitive_levels ?? [];

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('metadata.title')}
        description={t('metadata.desc')}
      />

      <Card className="p-5">
        <Tabs
          variant="underline"
          tabs={[
            {
              id: 'subjects',
              label: t('metadata.tab.subjects'),
              icon: <Layers className="w-4 h-4" />,
              badge: <Badge tone="neutral">{subjects.length}</Badge>,
              content: (
                <CrudList
                  items={subjects}
                  loading={meta.isLoading}
                  emptyText={t('metadata.empty.subjects')}
                  onAdd={() => setEditing({ type: 'subject-groups', row: {} })}
                  renderRow={(s: SubjectGroup) => (
                    <>
                      <div className="w-10 h-10 rounded-lg bg-brand-100 text-brand-700 flex items-center justify-center font-semibold flex-shrink-0">
                        {s.code?.slice(0, 3)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{s.name}</div>
                        {s.description && <div className="text-xs text-slate-500 truncate">{s.description}</div>}
                      </div>
                    </>
                  )}
                  onEdit={(row) => setEditing({ type: 'subject-groups', row })}
                  onDelete={(row) => handleDelete('subject-groups', row.id, row.name)}
                />
              ),
            },
            {
              id: 'grades',
              label: t('metadata.tab.grades'),
              icon: <GraduationCap className="w-4 h-4" />,
              badge: <Badge tone="neutral">{grades.length}</Badge>,
              content: (
                <CrudList
                  items={grades}
                  loading={meta.isLoading}
                  emptyText={t('metadata.empty.grades')}
                  onAdd={() => setEditing({ type: 'grade-levels', row: {} })}
                  renderRow={(g: GradeLevel) => (
                    <>
                      <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold flex items-center justify-center flex-shrink-0">
                        {g.level_order}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{g.name}</div>
                      </div>
                    </>
                  )}
                  onEdit={(row) => setEditing({ type: 'grade-levels', row })}
                  onDelete={(row) => handleDelete('grade-levels', row.id, row.name)}
                />
              ),
            },
            {
              id: 'difficulty',
              label: t('metadata.tab.difficulty'),
              icon: <Activity className="w-4 h-4" />,
              badge: <Badge tone="neutral">{difficulties.length}</Badge>,
              content: (
                <CrudList
                  items={difficulties}
                  loading={meta.isLoading}
                  emptyText={t('metadata.empty.difficulty')}
                  onAdd={() => setEditing({ type: 'difficulty-levels', row: {} })}
                  renderRow={(d: DifficultyLevel) => (
                    <>
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: d.color ?? '#94a3b8' }} />
                      <div className="flex-1 min-w-0 flex items-center gap-3">
                        <span className="text-sm font-medium text-slate-900">{d.name}</span>
                        <span className="text-xs text-slate-400 font-mono">{d.code}</span>
                      </div>
                    </>
                  )}
                  onEdit={(row) => setEditing({ type: 'difficulty-levels', row })}
                  onDelete={(row) => handleDelete('difficulty-levels', row.id, row.name)}
                />
              ),
            },
            {
              id: 'cognitive',
              label: t('metadata.tab.cognitive'),
              icon: <Brain className="w-4 h-4" />,
              badge: <Badge tone="neutral">{cognitives.length}</Badge>,
              content: (
                <CrudList
                  items={[...cognitives].sort((a, b) => (a.bloom_level ?? 0) - (b.bloom_level ?? 0))}
                  loading={meta.isLoading}
                  emptyText={t('metadata.empty.cognitive')}
                  onAdd={() => setEditing({ type: 'cognitive-levels', row: {} })}
                  renderRow={(c: CognitiveLevel) => (
                    <>
                      <span
                        className="w-7 h-7 rounded-md text-white text-xs font-semibold flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: c.color ?? '#94a3b8' }}
                      >
                        {c.bloom_level ?? '·'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900">
                          {c.name}{c.name_en ? ` · ${c.name_en}` : ''}
                        </div>
                      </div>
                    </>
                  )}
                  onEdit={(row) => setEditing({ type: 'cognitive-levels', row })}
                  onDelete={(row) => handleDelete('cognitive-levels', row.id, row.name)}
                />
              ),
            },
          ]}
        />
      </Card>

      {editing && (
        <MetadataEditModal
          target={editing}
          onClose={() => setEditing(null)}
          onSave={(data) => save.mutate({ type: editing.type, data, id: editing.row.id })}
          saving={save.isPending}
        />
      )}
    </div>
  );
}

interface CrudListProps<T> {
  items: T[];
  loading: boolean;
  emptyText?: string;
  renderRow: (item: T) => React.ReactNode;
  onAdd: () => void;
  onEdit: (item: T) => void;
  onDelete: (item: T) => void;
}
function CrudList<T extends { id: number }>({
  items, loading, emptyText, renderRow, onAdd, onEdit, onDelete,
}: CrudListProps<T>) {
  const t = useT();
  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={onAdd}>
          {t('metadata.add_new')}
        </Button>
      </div>
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : items.length === 0 ? (
        <EmptyState title={emptyText ?? t('empty.no_data')} action={{ label: t('metadata.add_new'), onClick: onAdd }} />
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((item) => (
            <li key={item.id} className="py-3 flex items-center gap-4 group">
              {renderRow(item)}
              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 flex-shrink-0">
                <button type="button" onClick={() => onEdit(item)} aria-label={t('btn.edit')}
                        className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={() => onDelete(item)} aria-label={t('btn.delete')}
                        className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MetadataEditModal({
  target, onClose, onSave, saving,
}: {
  target: EditTarget;
  onClose: () => void;
  onSave: (data: any) => void;
  saving: boolean;
}) {
  const t = useT();
  const isEdit = !!target.row.id;
  const [form, setForm] = useState<any>({ ...target.row });

  function set<K extends string>(k: K, v: any) {
    setForm((f: any) => ({ ...f, [k]: v }));
  }

  const titles: Record<ItemType, string> = {
    'subject-groups': isEdit ? t('metadata.modal.subject_edit') : t('metadata.modal.subject_add'),
    'grade-levels': isEdit ? t('metadata.modal.grade_edit') : t('metadata.modal.grade_add'),
    'difficulty-levels': isEdit ? t('metadata.modal.difficulty_edit') : t('metadata.modal.difficulty_add'),
    'cognitive-levels': isEdit ? t('metadata.modal.cognitive_edit') : t('metadata.modal.cognitive_add'),
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={titles[target.type]}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('btn.cancel')}</Button>
          <Button loading={saving} onClick={() => onSave(form)}>{t('btn.save')}</Button>
        </>
      }
    >
      <div className="space-y-4">
        {target.type === 'subject-groups' && (
          <>
            <Input label={t('metadata.field.subject_name')} value={form.name ?? ''} onChange={(e) => set('name', e.target.value)} required autoFocus />
            <Input label={t('metadata.field.code')} value={form.code ?? ''} onChange={(e) => set('code', e.target.value)} />
            <Textarea label={t('metadata.field.description')} value={form.description ?? ''} onChange={(e) => set('description', e.target.value)} rows={3} />
            <Input label={t('metadata.field.sort_order')} type="number" value={form.sort_order ?? 0} onChange={(e) => set('sort_order', parseInt(e.target.value, 10) || 0)} />
          </>
        )}
        {target.type === 'grade-levels' && (
          <>
            <Input label={t('metadata.field.grade_name')} value={form.name ?? ''} onChange={(e) => set('name', e.target.value)} required autoFocus />
            <Input label={t('metadata.field.level_order')} type="number" min={1} value={form.level_order ?? 1} onChange={(e) => set('level_order', parseInt(e.target.value, 10) || 1)} required />
            <Input label={t('metadata.field.level_code')} value={form.level_code ?? ''} onChange={(e) => set('level_code', e.target.value)} />
            <Textarea label={t('metadata.field.description')} value={form.description ?? ''} onChange={(e) => set('description', e.target.value)} rows={2} />
          </>
        )}
        {target.type === 'difficulty-levels' && (
          <>
            <Input label={t('metadata.field.name')} value={form.name ?? ''} onChange={(e) => set('name', e.target.value)} required autoFocus />
            <Input label={t('metadata.field.code')} value={form.code ?? ''} onChange={(e) => set('code', e.target.value)} required />
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('metadata.field.color')}</label>
              <input type="color" value={form.color ?? '#94a3b8'} onChange={(e) => set('color', e.target.value)} className="h-10 w-20 rounded border border-slate-200" />
            </div>
            <Textarea label={t('metadata.field.description')} value={form.description ?? ''} onChange={(e) => set('description', e.target.value)} rows={2} />
            <Input label={t('metadata.field.sort_order')} type="number" value={form.sort_order ?? 0} onChange={(e) => set('sort_order', parseInt(e.target.value, 10) || 0)} />
          </>
        )}
        {target.type === 'cognitive-levels' && (
          <>
            <Input label={t('metadata.field.th_name')} value={form.name ?? ''} onChange={(e) => set('name', e.target.value)} required autoFocus />
            <Input label={t('metadata.field.en_name')} value={form.name_en ?? ''} onChange={(e) => set('name_en', e.target.value)} />
            <Input label={t('metadata.field.code')} value={form.code ?? ''} onChange={(e) => set('code', e.target.value)} required />
            <Input label={t('metadata.field.bloom')} type="number" min={1} max={6} value={form.bloom_level ?? 1} onChange={(e) => set('bloom_level', parseInt(e.target.value, 10) || 1)} required />
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('metadata.field.color')}</label>
              <input type="color" value={form.color ?? '#94a3b8'} onChange={(e) => set('color', e.target.value)} className="h-10 w-20 rounded border border-slate-200" />
            </div>
            <Textarea label={t('metadata.field.description')} value={form.description ?? ''} onChange={(e) => set('description', e.target.value)} rows={2} />
          </>
        )}
      </div>
    </Modal>
  );
}
